<script setup lang="ts">
import { ChevronsUpDown, Users } from "@lucide/vue";

import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import type { ZenithTeamsTeam } from "@/api/model/zenithTeamsTeam";
import { useTeamStore } from "@/stores/team";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

const props = defineProps<{
  teams: ZenithTeamsTeam[];
}>();

const { isMobile } = useSidebar();
const { t } = useI18n();
const router = useRouter();
const teamStore = useTeamStore();

// The active team is the current-team context; fall back to the first team if
// the stored id is not in the list (e.g. membership changed).
const activeTeam = computed(
  () => props.teams.find((t) => t.id === teamStore.currentTeamId) ?? props.teams[0],
);

function switchTo(team: ZenithTeamsTeam) {
  if (!team.id || team.id === teamStore.currentTeamId) return;
  teamStore.setCurrent(team.id);
  // Reset to home so team-scoped views reload in the new team's context.
  router.push({ name: "home" });
}
</script>

<template>
  <SidebarMenu>
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <SidebarMenuButton
            size="lg"
            :aria-label="activeTeam?.name ?? t('teamSwitcher.noTeam')"
            class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <div
              class="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"
            >
              <Users class="size-4" />
            </div>
            <div class="grid flex-1 text-left text-sm leading-tight">
              <span class="truncate font-medium">
                {{ activeTeam?.name ?? t("teamSwitcher.noTeam") }}
              </span>
            </div>
            <ChevronsUpDown class="ml-auto" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="w-[--reka-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          align="start"
          :side="isMobile ? 'bottom' : 'right'"
          :side-offset="4"
        >
          <DropdownMenuLabel class="text-xs text-muted-foreground">
            {{ t("teamSwitcher.teamsLabel") }}
          </DropdownMenuLabel>
          <DropdownMenuItem
            v-for="team in teams"
            :key="team.id ?? team.name"
            class="gap-2 p-2"
            @click="switchTo(team)"
          >
            <div class="flex size-6 items-center justify-center rounded-sm border">
              <Users class="size-3.5 shrink-0" />
            </div>
            {{ team.name }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  </SidebarMenu>
</template>
